import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { LoaderCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/client';
import { useCreateSupplier, useUpdateSupplier } from '@/api/hooks/useSuppliers';
import type { Supplier } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';

export function SupplierFormDialog({
  supplier,
  open,
  onOpenChange,
}: {
  supplier?: Supplier;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const isEdit = supplier !== undefined;
  const createSupplier = useCreateSupplier();
  const updateSupplier = useUpdateSupplier();

  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(supplier?.name ?? '');
      setContact(supplier?.contact ?? '');
      setEmail(supplier?.email ?? '');
      setAddress(supplier?.address ?? '');
      setError(null);
    }
  }, [open, supplier]);

  const submitting = createSupplier.isPending || updateSupplier.isPending;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (!name.trim() || !contact.trim()) {
      setError('Name and contact number are required.');
      return;
    }

    const payload = {
      name: name.trim(),
      contact: contact.trim(),
      email: email.trim() || undefined,
      address: address.trim() || undefined,
    };

    try {
      if (isEdit) {
        await updateSupplier.mutateAsync({ id: supplier!.id, request: payload });
        toast({ title: 'Supplier updated', variant: 'success' });
      } else {
        await createSupplier.mutateAsync(payload);
        toast({ title: 'Supplier added', variant: 'success' });
      }
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit supplier' : t('addSupplier')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="s-name">{t('supplierName')} *</Label>
            <Input id="s-name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-contact">Contact number *</Label>
            <Input id="s-contact" value={contact} onChange={(e) => setContact(e.target.value)} required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-email">{t('email')}</Label>
            <Input id="s-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-address">{t('address')}</Label>
            <Input id="s-address" value={address} onChange={(e) => setAddress(e.target.value)} />
          </div>

          {error && (
            <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <LoaderCircle className="animate-spin" />}
              {isEdit ? 'Save changes' : t('addSupplier')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
